import React, { useState, useEffect, useCallback  } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  Divider,
  useTheme,
  Alert,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper
} from '@mui/material';
import {
  Add,
  Search,
  MoreVert,
  Edit,
  Delete,
  Visibility,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import menuService from '../../services/menuService';

// Import dialog components
import EditMenu from './dialogs/EditMenu';
import ViewDetails from './dialogs/ViewDetails';
import DeleteDialog from './dialogs/DeleteDialog';
import SuccessDialog from './dialogs/SuccessDialog';

const MenuList = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const MotionDiv = motion.div;
  
  // State for error handling
  const [errorMessage, setErrorMessage] = useState(null);
  
  // State for menu items
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  
  // State for categories
  const [categories, setCategories] = useState([]);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [recipeCategoryFilter, setRecipeCategoryFilter] = useState('all');
  
  // Sort state
  const [sortOption, setSortOption] = useState('nameAsc');
  
  // View type state (grid or list)
  const [viewType, setViewType] = useState('grid');
  
  // Action menu state
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // ✅ NEW: Recipe category display function
  const getRecipeCategoryDisplay = (recipeCategory) => {
    switch(recipeCategory) {
      case 'raw_materials': 
        return t('inventory.recipe.rawMaterials');
      case 'premade_items': 
        return t('inventory.recipe.premadeItems');
      case 'finished_goods':
        return t('inventory.recipe.finishedGoods');
      case null:
      case undefined:
      case '':
        return t('inventory.recipe.uncategorized');
      default: 
        return t('inventory.recipe.uncategorized');
    }
  };
  

   // ✅ ENHANCED: Recipe category color mapping
  const getRecipeCategoryColor = (recipeCategory) => {
    switch(recipeCategory) {
      case 'raw_materials': return 'primary';
      case 'premade_items': return 'secondary';
      case 'finished_goods': return 'success';
      default: return 'default';
    }
  };
  // Fetch menu items and categories on component mount
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [menuData, categoriesData] = await Promise.all([
        menuService.getAllWithCategories(),
        menuService.getAllCategories()
      ]);
      
      setMenuItems(menuData);
      setCategories(categoriesData);
      setErrorMessage(null);
    } catch (error) {
      console.error('Error fetching data:', error);
      setErrorMessage(t('menu.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ✅ UPDATED: Apply filters and sorting with recipe category filter
  const applyFilters = useCallback(() => {
    let filtered = [...menuItems];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => 
        item.category_id.toString() === categoryFilter
      );
    }
    
    // Apply availability filter
    if (availabilityFilter !== 'all') {
      filtered = filtered.filter(item => {
        const isAvailable = item.available === 1 || item.available === true;
        return availabilityFilter === 'available' ? isAvailable : !isAvailable;
      });
    }
    
    // ✅ NEW: Recipe category filter
    if (recipeCategoryFilter !== 'all') {
      filtered = filtered.filter(item => 
        (item.recipe_category || 'uncategorized') === recipeCategoryFilter
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortOption) {
        case 'nameAsc':
          return a.name.localeCompare(b.name);
        case 'nameDesc':
          return b.name.localeCompare(a.name);
        case 'priceAsc':
          return a.price - b.price;
        case 'priceDesc':
          return b.price - a.price;
        default:
          return 0;
      }
    });
    
    setFilteredItems(filtered);
  }, [menuItems, searchTerm, categoryFilter, availabilityFilter, recipeCategoryFilter, sortOption]);
  
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle category filter change
  const handleCategoryChange = (e) => {
    setCategoryFilter(e.target.value);
  };
  
  // Handle availability filter change
  const handleAvailabilityChange = (e) => {
    setAvailabilityFilter(e.target.value);
  };
  
  // Handle sort option change
  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };
  
  // Handle add menu item button click
  const handleAddItem = () => {
    navigate('/dashboard/menu/add');
  };
  
  // Handle action menu open
  const handleActionMenuOpen = (event, item) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };
  
  // Handle action menu close
  const handleActionMenuClose = () => {
    setActionAnchorEl(null);
    // Don't clear selectedItem here - it causes delete dialog to fail
  };
  
  // Handle view item
  const handleViewItem = () => {
    setViewDialogOpen(true);
    setActionAnchorEl(null); // Only close the menu, keep the selected item
  };
  
  // Handle edit item
  const handleEditItem = () => {
    setEditDialogOpen(true);
    setActionAnchorEl(null); // Only close the menu, keep the selected item
  };
  
  // Handle delete item
  const handleDeleteItem = () => {
    setDeleteDialogOpen(true);
    setActionAnchorEl(null); // Only close the menu, keep the selected item
  };

  const handleDeleteMenuItem = async (itemId) => {
    try {
      await menuService.delete(itemId);
      // setSuccessMessage(t('menu.deleteSuccess'));
      // setSuccessDialogOpen(true);
      fetchData(); // Refresh data
      return true;
    } catch (error) {
      console.error('Error deleting menu item:', error);
      setErrorMessage(t('menu.deleteError'));
      return false;
    }
  };
  
  // Handle toggle availability
  const handleToggleAvailability = async (item) => {
    try {
      await menuService.toggleAvailability(item.item_id);
      const newStatus = item.available ? t('status.unavailable') : t('status.available');
      setSuccessMessage(t('menu.availabilityToggleSuccess', { 
        itemName: item.name, 
        status: newStatus 
      }));
      setSuccessDialogOpen(true);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error toggling availability:', error);
      setErrorMessage(t('menu.availabilityToggleError'));
    }
  };
  
  // Handle successful operations
  const handleSuccess = (message) => {
    setSuccessMessage(message);
    setSuccessDialogOpen(true);
    fetchData(); // Refresh data
  };

  // Render grid item component
  const renderGridItem = (item) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={item.item_id}>
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card 
          sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.shadows[8]
            }
          }}
          onClick={() => {
            setSelectedItem(item);
            setViewDialogOpen(true);
          }}
        >
          <Box sx={{ position: 'relative' }}>
            {item.image ? (
              <Box
                component="img"
                sx={{
                  height: 200,
                  width: '100%',
                  objectFit: 'cover',
                }}
                src={item.image}
                alt={item.name}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            
            <Box
              sx={{
                height: 200,
                width: '100%',
                display: item.image ? 'none' : 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.100',
                color: 'grey.500'
              }}
            >
              <Typography variant="body2">
                {t('menu.noImage')}
              </Typography>
            </Box>
            
            {!item.available && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(0,0,0,0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {t('menu.unavailable')}
                </Typography>
              </Box>
            )}
            
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleActionMenuOpen(e, item);
              }}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { bgcolor: 'background.paper' }
              }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Box>
          
          <CardContent sx={{ flexGrow: 1, p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography 
                  variant="h6" 
                  component="h3" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    lineHeight: 1.3,
                    mb: 1
                  }}
                >
                  {item.name}
                </Typography>
                
                <Typography 
                  variant="body1" 
                  color="primary.main" 
                  sx={{ fontWeight: 500, mb: 1 }}
                >
                  ${parseFloat(item.price).toFixed(2)}
                </Typography>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {item.description || t('menu.noDescription')}
                </Typography>
                
                {item.variations && item.variations.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      {t('menu.variations')}:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {item.variations.slice(0, 2).map((variation, index) => (
                        <Chip
                          key={index}
                          label={`${variation.name} - $${parseFloat(variation.price).toFixed(2)}`}
                          size="small"
                          sx={{ 
                            fontSize: '0.7rem', 
                            height: 20,
                            '& .MuiChip-label': {
                              px: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }
                          }}
                        />
                      ))}
                      {item.variations.length > 2 && (
                        <Chip
                          label={t('menu.moreVariations', { count: item.variations.length - 2 })}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Chip 
                  label={item.category_name || t('menu.uncategorized')} 
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
                {/* ✅ NEW: Add recipe category to grid view */}
                <Chip 
                  label={getRecipeCategoryDisplay(item.recipe_category)}
                  size="small"
                  color={getRecipeCategoryColor(item.recipe_category)}
                  variant="outlined"
                  sx={{ borderRadius: 1 }}
                />
              </Box>
            </Box>
            
            {/* Action buttons */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              mt: 1
            }}>
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleAvailability(item);
                }}
                fullWidth
              >
                {item.available ? t('menu.markUnavailable') : t('menu.markAvailable')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </MotionDiv>
    </Grid>
  );

  // ✅ UPDATED: Render list view with recipe category column
  const renderListView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('menu.table.name')}</TableCell>
            <TableCell>{t('menu.table.category')}</TableCell>
            <TableCell>{t('menu.table.recipeType')}</TableCell> {/* ✅ NEW */}
            <TableCell align="right">{t('menu.table.price')}</TableCell>
            <TableCell align="center">{t('menu.table.status')}</TableCell>
            <TableCell align="center">{t('menu.table.actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredItems.map(item => (
            <TableRow key={item.item_id}>
              <TableCell>{item.name}</TableCell>
              <TableCell>
                <Chip 
                  label={item.category_name || t('menu.uncategorized')} 
                  size="small" 
                  sx={{ borderRadius: 1 }}
                />
              </TableCell>
              {/* ✅ NEW: Recipe category column */}
              <TableCell>
                <Chip 
                  label={getRecipeCategoryDisplay(item.recipe_category)}
                  size="small"
                  color={getRecipeCategoryColor(item.recipe_category)}
                  variant="outlined"
                  sx={{ 
                    borderRadius: 1,
                    minWidth: '100px'
                  }}
                />
              </TableCell>
              <TableCell align="right">${parseFloat(item.price).toFixed(2)}</TableCell>
              <TableCell align="center">
                <Chip 
                  label={item.available ? t('status.available') : t('status.unavailable')} 
                  size="small"
                  color={item.available ? 'success' : 'error'}
                  sx={{ borderRadius: 1 }}
                />
              </TableCell>
              <TableCell align="center">
                <IconButton
                  size="small"
                  onClick={(e) => handleActionMenuOpen(e, item)}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <MotionDiv
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              {t('menu.listTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('menu.listSubtitle')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddItem}
            size="large"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {t('menu.addMenuItem')}
          </Button>
        </Box>
      </MotionDiv>

      {/* Filters */}
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder={t('menu.searchPlaceholder')}
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('menu.category')}</InputLabel>
                  <Select
                    value={categoryFilter}
                    onChange={handleCategoryChange}
                    label={t('menu.category')}
                  >
                    <MenuItem value="all">{t('menu.allCategories')}</MenuItem>
                    {categories.map(category => (
                      <MenuItem key={category.category_id} value={category.category_id.toString()}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('menu.availability')}</InputLabel>
                  <Select
                    value={availabilityFilter}
                    onChange={handleAvailabilityChange}
                    label={t('menu.availability')}
                  >
                    <MenuItem value="all">{t('menu.allItems')}</MenuItem>
                    <MenuItem value="available">{t('status.available')}</MenuItem>
                    <MenuItem value="unavailable">{t('status.unavailable')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* ✅ NEW: Recipe category filter */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('menu.recipeCategory')}</InputLabel>
                  <Select
                    value={recipeCategoryFilter}
                    onChange={(e) => setRecipeCategoryFilter(e.target.value)}
                    label={t('menu.recipeCategory')}
                  >
                    <MenuItem value="all">{t('menu.allTypes')}</MenuItem>
                    <MenuItem value="raw_materials">{t('inventory.recipe.rawMaterials')}</MenuItem>
                    <MenuItem value="premade_items">{t('inventory.recipe.premadeItems')}</MenuItem>
                    <MenuItem value="uncategorized">{t('inventory.recipe.uncategorized')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('menu.sortBy')}</InputLabel>
                  <Select
                    value={sortOption}
                    onChange={handleSortChange}
                    label={t('menu.sortBy')}
                  >
                    <MenuItem value="nameAsc">{t('menu.nameAZ')}</MenuItem>
                    <MenuItem value="nameDesc">{t('menu.nameZA')}</MenuItem>
                    <MenuItem value="priceAsc">{t('menu.priceLowHigh')}</MenuItem>
                    <MenuItem value="priceDesc">{t('menu.priceHighLow')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={1}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <IconButton 
                    color="primary"
                    onClick={() => setViewType(viewType === 'grid' ? 'list' : 'grid')}
                    title={viewType === 'grid' ? t('menu.switchToListView') : t('menu.switchToGridView')}
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </MotionDiv>
      
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {isLoading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography>{t('menu.loadingItems')}</Typography>
          </Box>
        ) : filteredItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography>{t('menu.noItemsFound')}</Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('all');
                setAvailabilityFilter('all');
                setRecipeCategoryFilter('all');
                setSortOption('nameAsc');
              }}
              sx={{ mt: 2 }}
            >
              {t('menu.clearFilters')}
            </Button>
          </Box>
        ) : (
          viewType === 'grid' ? (
            <Grid container spacing={3}>
              {filteredItems.map(item => renderGridItem(item))}
            </Grid>
          ) : (
            renderListView()
          )
        )}
      </MotionDiv>
      
      {/* Action Menu */}
      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleActionMenuClose}
        PaperProps={{
          sx: { width: 160, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: 2 }
        }}
      >
        <MenuItem onClick={handleViewItem}>
          <Visibility fontSize="small" sx={{ mr: 1.5 }} />
          {t('menu.viewDetails')}
        </MenuItem>
        <MenuItem onClick={handleEditItem}>
          <Edit fontSize="small" sx={{ mr: 1.5 }} />
          {t('common.edit')}
        </MenuItem>
        <MenuItem onClick={handleDeleteItem} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1.5 }} />
          {t('common.delete')}
        </MenuItem>
      </Menu>
      
      {/* Error Alert */}
      {errorMessage && (
        <Alert 
          severity="error" 
          onClose={() => setErrorMessage(null)}
          sx={{ mt: 2 }}
        >
          {errorMessage}
        </Alert>
      )}
      
        {/* Dialogs */}
      <EditMenu
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          // Only reset selectedItem when dialog closes
          if (!viewDialogOpen && !deleteDialogOpen) {
            setSelectedItem(null);
          }
        }}
        item={selectedItem}
        onSuccess={handleSuccess}
      />
      
      <ViewDetails
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          // Only reset selectedItem when dialog closes
          if (!editDialogOpen && !deleteDialogOpen) {
            setSelectedItem(null);
          }
        }}
        item={selectedItem}
        categories={categories}
        onEdit={() => {
          setViewDialogOpen(false);
          setEditDialogOpen(true);
        }}
      />
      
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          // Only reset selectedItem when dialog closes
          if (!editDialogOpen && !viewDialogOpen) {
            setSelectedItem(null);
          }
        }}
        item={selectedItem}
        onDelete={handleDeleteMenuItem}
      />
      
      <SuccessDialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
        message={successMessage}
      />
    </Box>
  );
};

export default MenuList;